import React from 'react';

import useDateFormat from '../../hooks/useDateFormat';
import { formatDisplayDate, formatSourceDate } from '../../utils/dateDisplay';
import { CPU, FORM } from '../icons';
import UserRef from '../users/UserRef';
import ExternalLink from '../common/ExternalLink';

const isHttpUrl = value => typeof value === 'string' && /^https?:\/\//i.test(value);

/**
 * Provenance for an item a machine client wrote: `source` is
 * { system, externalId, ref?, ingestedAt } when the item came in through the
 * ingest API, and absent on everything a person typed. Renders nothing when
 * there is nothing to say, so every surface can drop it in unconditionally.
 *
 * "Imported by <system> · <externalId> · <date>", plus `ref` as a link when
 * it is an http(s) URL (a plain-text ref is shown as-is). When the source
 * system said when the record was made there (`source.createdAt`), that date
 * leads — "Created in <system> on <date> · added to keepr <date> · <id>" —
 * because an old record imported today is not a record made today. It is
 * provenance beside keepr's own dates, never a replacement for them.
 *
 * `intake` is the OTHER provenance: someone without an account sent this
 * through an intake link. It wins when present — "a stranger filled a form
 * in" is a different and more urgent fact than "a machine imported a row",
 * and the raw source line would say it as the internal `keepr.intake` plus a
 * hash. The link's name and creator are SNAPSHOTS taken at submit time (the
 * link itself is reaped at expiry; the item lives on), and the bot check is
 * reported as what it was: "skipped" is never dressed up as "passed".
 */
export default function ItemSourceLine({ source, intake = null, className = '' }) {
	useDateFormat(); // re-render when the date-format preference changes

	if (intake && typeof intake === 'object' && (intake.submittedAt || intake.linkName || intake.reviewState)) {
		const pending = intake.reviewState === 'pending';
		const parts = [
			<span key="via">
				Sent through {intake.linkName ? <strong>{intake.linkName}</strong> : 'an intake link'}
			</span>,
		];
		if (intake.submittedAt) parts.push(<span key="date">{formatDisplayDate(intake.submittedAt)}</span>);
		if (intake.linkCreatedBy) {
			parts.push(
				<span key="creator" className="item-source__who">
					link by <UserRef userId={intake.linkCreatedBy} size={16} />
				</span>
			);
		}
		if (intake.challenge === 'passed') parts.push(<span key="bot">bot check passed</span>);
		else if (intake.challenge === 'skipped') parts.push(<span key="bot">no bot check was run</span>);
		if (!pending && intake.reviewState === 'accepted') {
			parts.push(
				<span key="review" className="item-source__who">
					accepted{intake.reviewedAt ? ` ${formatDisplayDate(intake.reviewedAt)}` : ''}
					{intake.reviewedBy ? <> by <UserRef userId={intake.reviewedBy} size={16} /></> : null}
				</span>
			);
		}
		return (
			<div className={['item-source', 'item-source--intake', className].filter(Boolean).join(' ')} data-testid="item-intake-source">
				<FORM aria-hidden="true" className="item-source__icon" />
				{parts.map((part, index) => (
					<React.Fragment key={part.key}>
						{index > 0 ? <span className="item-source__sep" aria-hidden="true"> · </span> : null}
						{part}
					</React.Fragment>
				))}
			</div>
		);
	}

	if (!source || typeof source !== 'object') return null;
	const { system, externalId, ref, ingestedAt, createdAt } = source;
	if (!system && !externalId) return null;

	const parts = [];
	if (createdAt) {
		parts.push(<span key="created">Created in <strong>{system || 'its source'}</strong> on {formatSourceDate(createdAt)}</span>);
		if (ingestedAt) parts.push(<span key="date">added to keepr {formatDisplayDate(ingestedAt)}</span>);
		if (externalId) parts.push(<span key="external-id" className="item-source__id">{externalId}</span>);
	} else {
		if (system) parts.push(<span key="system">Imported by <strong>{system}</strong></span>);
		if (externalId) parts.push(<span key="external-id" className="item-source__id">{externalId}</span>);
		if (ingestedAt) parts.push(<span key="date">{formatDisplayDate(ingestedAt)}</span>);
	}
	if (isHttpUrl(ref)) {
		parts.push(
			<ExternalLink key="ref" href={ref} className="item-source__ref">
				Source
			</ExternalLink>
		);
	} else if (typeof ref === 'string' && ref.trim()) {
		parts.push(<span key="ref" className="item-source__ref">{ref}</span>);
	}

	const classes = ['item-source', className].filter(Boolean).join(' ');

	return (
		<div className={classes} data-testid="item-source">
			<CPU aria-hidden="true" className="item-source__icon" />
			{parts.map((part, index) => (
				<React.Fragment key={part.key}>
					{index > 0 ? <span className="item-source__sep" aria-hidden="true"> · </span> : null}
					{part}
				</React.Fragment>
			))}
		</div>
	);
}
